import { faFeather } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H1, H2, SmallRegular } from "../../../styles/Fonts";
import PoolBalanceItem from "./PoolBalanceItem";
import FactoryPositionItem from "./FactoryPositionItem";
import useLendingPools from "../../../hooks/useLendingPool";
import useFactoryPairs from "../../../hooks/useFactoryPairs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getFactoryPositions } from "../../../redux/factoryAction";
import { useWeb3React } from "@web3-react/core";
import { PairProps } from "../../../utils/constant/pairs";
import { UserActivePositionProps } from "../../../redux/factorySlice";

const positionHeaders = [
  "Pair",
  "Position Value",
  "Earned Fee",
  "Fee APR",
  "Leverage",
];

const Portfolio: React.FC = () => {
  const pools = useLendingPools();
  const pairs = useFactoryPairs();
  const { account, library, chainId } = useWeb3React();

  const activePositions = useAppSelector(
    (state) => state.factory.userActivePositions
  );

  const dispatch = useAppDispatch();

  const updateUserActivePositions = useCallback(
    async (
      factoryAddress: string,
      wantTokenAddress: string,
      wantTokenDecimal: number,
      borrowTokenAddress: string,
      borrowTokenDecimal: number,
      uniPoolAddress: string,
      lendingPoolAddress: string
    ) => {
      if (
        !(
          account &&
          factoryAddress &&
          wantTokenAddress &&
          wantTokenDecimal &&
          borrowTokenAddress &&
          borrowTokenDecimal &&
          uniPoolAddress &&
          lendingPoolAddress &&
          chainId &&
          library
        )
      )
        return;

      dispatch(
        getFactoryPositions(
          account,
          factoryAddress,
          wantTokenAddress,
          wantTokenDecimal,
          borrowTokenAddress,
          borrowTokenDecimal,
          uniPoolAddress,
          lendingPoolAddress,
          chainId,
          library
        )
      );
    },
    [account, dispatch, chainId, library]
  );

  useEffect(() => {
    pairs.forEach((pair) => {
      const {
        factoryAddress,
        wantToken,
        borrowToken,
        uniPoolAddress,
        lendingPoolAddress,
      } = pair;
      updateUserActivePositions(
        factoryAddress,
        wantToken.tokenAddress,
        wantToken.tokenDecimal,
        borrowToken.tokenAddress,
        borrowToken.tokenDecimal,
        uniPoolAddress,
        lendingPoolAddress
      );
    });
  }, [pairs, updateUserActivePositions]);

  const positions = useMemo(() => {
    if (!chainId) return [];

    const positionsUnderChain = activePositions[chainId];

    const array: {
      pair: PairProps;
      position: UserActivePositionProps;
    }[] = [];

    pairs.forEach((pair) => {
      const factoryPositions = positionsUnderChain?.[pair.factoryAddress];
      if (factoryPositions?.length > 0) {
        factoryPositions.forEach((pos) => {
          if (pos) {
            array.push({ pair, position: pos });
          }
        });
      }
    });
    return array;
  }, [activePositions, chainId, pairs]);

  return (
    <Container>
      <PortfolioContainer>
        <Title>Portfolio</Title>
        <PoolContainer>
          <PortfolioTitleRow>
            <Icon icon={faFeather} />
            <Subtitle>Pool Balance</Subtitle>
          </PortfolioTitleRow>
          <PortfolioHeaderRow>
            <HeaderText>Asset</HeaderText>
            <HeaderText>ROI</HeaderText>
            <HeaderText>Balance</HeaderText>
            <HeaderText>Earned</HeaderText>
          </PortfolioHeaderRow>
          {pools.map((pool) => (
            <PoolBalanceItem pool={pool} key={pool.lendingPoolAddress} />
          ))}
        </PoolContainer>
        <GrayContainer>
          <PortfolioTitleRow>
            <Icon icon={faFeather} />
            <Subtitle>Factory Positions</Subtitle>
          </PortfolioTitleRow>
          <PositionHeaderRow>
            {positionHeaders.map((header) => (
              <HeaderText key={header}>{header}</HeaderText>
            ))}
          </PositionHeaderRow>
          {positions.length > 0 ? (
            positions.map((pos) => (
              <FactoryPositionItem key={pos.position?.positionId} pos={pos} />
            ))
          ) : (
            <NoRecordContainer>
              <NoRecord>No Record</NoRecord>
            </NoRecordContainer>
          )}
        </GrayContainer>
        <GrayContainer>
          <PortfolioTitleRow>
            <Icon icon={faFeather} />
            <Subtitle>Vault Balance</Subtitle>
          </PortfolioTitleRow>
          <BalanceRow>
            <ValueText>Can Withdraw Amount</ValueText>
            <ValueText>100 USDC</ValueText>
          </BalanceRow>
          <BalanceRow>
            <ValueText>Unredeemed Amount</ValueText>
            <ValueText>100 USDC</ValueText>
          </BalanceRow>
        </GrayContainer>
      </PortfolioContainer>
    </Container>
  );
};

export default Portfolio;

const Container = styled.div``;

const GrayContainer = styled.div`
  border-radius: 8px;
  padding: 24px;
  background-color: ${Colors.backgroundGray1};
`;

const Title = styled(H1)`
  font-size: 24px;
  line-height: 32px;
  text-align: left;
  color: ${Colors.ivory};
  font-family: TT Firs Neue, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  margin-bottom: 20px;
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
  @media (max-width: 576px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const PortfolioContainer = styled.div`
  margin-top: 60px;
  > div:not(:first-child) {
    margin-top: 24px;
  }
`;

const PortfolioRow = styled.div`
  display: grid;
  grid-template-columns: 100px repeat(3, 1fr);
  align-items: center;
  > span:not(:first-child) {
    text-align: right;
  }
`;

const PortfolioHeaderRow = styled(PortfolioRow)`
  padding: 0px 40px 0px 24px;
  margin-bottom: 12px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const PoolContainer = styled(GrayContainer)`
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PortfolioTitleRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 18px;
  position: relative;
  ::after {
    position: absolute;
    content: "";
    width: calc(100% + 48px);
    border-bottom: 0.5px solid ${Colors.gray5};
    top: 100%;
    left: -24px;
  }
`;

const Subtitle = styled(H2)`
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
  text-align: left;
  color: ${Colors.ivory};
`;

const Icon = styled(FontAwesomeIcon)`
  font-size: 18px;
  color: ${Colors.ivory};
  margin-left: 8px;
  margin-right: 12px;
`;

const HeaderText = styled(SmallRegular)`
  color: ${Colors.gray4};
`;

const ValueText = styled(SmallRegular)`
  color: ${Colors.ivory};
  font-size: 14px;
  @media (max-width: 768px) {
    text-align: right;
    font-size: 13px;
    line-height: 18px;
  }
`;

const PositionRow = styled.div`
  display: grid;
  grid-template-columns: 100px repeat(4, 1fr);
  align-items: center;
  > span:not(:first-child) {
    text-align: right;
  }
  @media (max-width: 768px) {
    display: block;
  }
`;

const PositionHeaderRow = styled(PositionRow)`
  padding: 0px 40px 0px 24px;
  margin-bottom: 12px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const NoRecordContainer = styled.div`
  text-align: center;
  padding: 40px 0;
`;

const NoRecord = styled.span`
  display: inline-block;
  padding: 4px 20px;
  border-radius: 8px;
  background-color: ${Colors.backgroundGray4};
  margin: auto;
  font-size: 14px;
  line-height: 22px;
  color: ${Colors.ivory};
`;
